import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { dismiss, notifyProblems } from '../lib/errorNotices';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canApproveDisbursements, formatAmount, parseAmount } from '@promanage/core';
import {
  BANK_FILE_FORMATS,
  BANK_FILE_FORMAT_LABELS,
  PAYEE_QUARANTINE_HOURS,
  type BankFileFormat,
} from '@promanage/core/disbursement';
import { Amount, Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { Icon } from '../components/Icon';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useOrgSettings } from '../features/settings/org';
import { useSession } from '../features/auth/session';
import { trpc } from '../lib/trpc';
import { SavedNote, useUnsavedChanges } from '../components/UnsavedChanges';
import { Checklist, ChecklistRow } from '../features/settings/Checklist';

export const Route = createFileRoute('/settings/payments')({
  component: PaymentsSettingsPage,
});

/**
 * The agency's payment controls — docs/SECURITY.md §5, stated as settings.
 *
 * TWO OF THEM ARE ADJUSTABLE AND FOUR ARE NOT, and showing them together is the point. An
 * agency reading this page is answering "what stops a payment going to the wrong place here", and
 * the honest answer includes the parts they cannot change: the 24-hour hold on a new bank account,
 * and the hash taken at approval. Listing only the knob would imply the rest is optional.
 *
 * The two knobs are the separation-of-duties threshold and the owner-approval threshold for
 * maintenance, and both are guarded by the authority they constrain rather than by the one that
 * uses them: letting the person a control constrains raise it makes it advisory. They sit at
 * opposite ends of the same repair — one binds the decision to SPEND an owner's money, the other
 * the payment that eventually settles it.
 */
/** One notice per form for its own checks, replaced rather than stacked on a second try. */
const DISBURSEMENT_THRESHOLD = 'disbursement-threshold';
const MAINTENANCE_THRESHOLD = 'maintenance-threshold';
const TRUST_ACCOUNT = 'trust-account';
const OPERATING_ACCOUNT = 'operating-account';

function PaymentsSettingsPage() {
  const org = useOrgSettings();
  const session = useSession();
  const mayChange = canApproveDisbursements(session?.roles ?? []);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Payments"
        subtitle={
          mayChange
            ? 'Bank accounts, approval limits and safeguards'
            : 'View only — a trust administrator can change these'
        }
      />

      <SettingsNav />

      {org.isError ? (
        <LoadFailed className="mb-4" retry={() => void org.refetch()} retrying={org.isFetching} />
      ) : null}

      {/* Bank accounts on the left, what must be agreed on the right — the section headings that
          used to float above each column are the panels' own titles now. */}
      <div className="grid items-start gap-4 xl:grid-cols-5">
        <div className="space-y-4 xl:col-span-3">
          <TrustAccountPanel mayChange={mayChange} />
          <OperatingAccountPanel mayChange={mayChange} />
        </div>
        <div className="space-y-4 xl:col-span-2">
          {org.isPending ? (
            <>
              <SettingsLoading label="Loading payment approval limit" />
              <SettingsLoading label="Loading maintenance approval limit" />
            </>
          ) : null}
          {org.data ? <ThresholdPanel settings={org.data} mayChange={mayChange} /> : null}
          {org.data ? (
            <MaintenanceThresholdPanel settings={org.data} mayChange={mayChange} />
          ) : null}
          <FixedControls />
        </div>
      </div>
    </div>
  );
}

function SettingsLoading({ label }: { label: string }) {
  return (
    <Panel className="p-5" role="status" aria-label={label}>
      <div aria-hidden="true" className="space-y-3 motion-safe:animate-pulse">
        <div className="h-4 w-1/2 rounded-sm bg-surface-sunk" />
        <div className="h-16 rounded-md bg-surface-sunk" />
        <div className="h-3 w-3/4 rounded-sm bg-surface-sunk" />
      </div>
    </Panel>
  );
}

function ThresholdPanel({
  settings,
  mayChange,
}: {
  settings: { dualApprovalThresholdCents: string; dualApprovalThresholdFormatted: string };
  mayChange: boolean;
}) {
  const form = useUnsavedChanges();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const save = useMutation(
    trpc.org.setDualApprovalThreshold.mutationOptions({
      meta: { errorTitle: 'Could not change the threshold' },
      onSuccess: () => {
        form.saved();
        dismiss(DISBURSEMENT_THRESHOLD);
        setEditing(false);
        void queryClient.invalidateQueries({ queryKey: trpc.org.settings.queryKey() });
      },
    }),
  );

  const everyRun = settings.dualApprovalThresholdCents === '0';

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Second approver"
        subtitle="For payment runs"
        aside={
          mayChange && !editing ? (
            <div className="flex items-center gap-3">
              {form.justSaved ? <SavedNote /> : null}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setValue(
                    everyRun ? '' : formatAmount(BigInt(settings.dualApprovalThresholdCents)),
                  );
                  setEditing(true);
                }}
              >
                Change
              </Button>
            </div>
          ) : null
        }
      />

      <div className="px-5 pb-5">
        <Limit
          value={
            everyRun ? (
              'Every run'
            ) : (
              <>
                <span className="text-[0.8125rem] font-medium text-ink-muted">Above </span>
                <Amount value={BigInt(settings.dualApprovalThresholdCents)} currency />
              </>
            )
          }
          note={
            everyRun
              ? 'Nobody approves their own run'
              : 'Below this, the proposer may approve their own run'
          }
        />

        {editing ? (
          <form
            onChange={form.touch}
            className="mt-4 flex flex-wrap items-end gap-2 rounded-md bg-surface-sunk p-4"
            onSubmit={(event) => {
              event.preventDefault();
              try {
                const cents = value.trim() === '' ? 0n : parseAmount(value);
                save.mutate({ thresholdCents: cents.toString() });
              } catch {
                notifyProblems(
                  'Could not change the disbursement threshold',
                  ['That is not an amount. Write it like 5,000.00'],
                  DISBURSEMENT_THRESHOLD,
                );
              }
            }}
          >
            <Field label="Threshold" hint="Empty means every run" className="w-full">
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  inputMode="decimal"
                  disabled={save.isPending}
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value);
                  }}
                  placeholder="0.00"
                  className="tabular-nums"
                />
              )}
            </Field>
            <Button size="sm" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving' : 'Save limit'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => {
                form.reset();
                setEditing(false);
                dismiss(DISBURSEMENT_THRESHOLD);
              }}
            >
              Cancel
            </Button>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * What the agency may authorise on a repair before it has to ring the owner.
 *
 * ON THE PAYMENTS PAGE BESIDE THE SECOND-APPROVER THRESHOLD, because an agency asking "what stops
 * money going out of here without the right person agreeing" is asking one question, and the two
 * answers sit at opposite ends of the same repair: this one binds the decision to SPEND, and the
 * one above binds the payment that eventually settles it.
 *
 * THREE STATES, NOT TWO, and the empty one is not "off". Not recorded means the agency has not
 * told us what its management agreement says, so nothing is refused — the state every agency
 * starts in. Zero means the agreement reserves every repair to the owner. Shipping a default here
 * would have been stating a term of somebody's contract we have never read.
 */
function MaintenanceThresholdPanel({
  settings,
  mayChange,
}: {
  settings: {
    maintenanceApprovalThresholdCents: string | null;
    maintenanceApprovalThresholdFormatted: string | null;
  };
  mayChange: boolean;
}) {
  const form = useUnsavedChanges();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const save = useMutation(
    trpc.org.setMaintenanceApprovalThreshold.mutationOptions({
      meta: { errorTitle: 'Could not change the threshold' },
      onSuccess: () => {
        form.saved();
        dismiss(MAINTENANCE_THRESHOLD);
        setEditing(false);
        void queryClient.invalidateQueries({ queryKey: trpc.org.settings.queryKey() });
      },
    }),
  );

  const recorded = settings.maintenanceApprovalThresholdCents !== null;
  const askAlways = settings.maintenanceApprovalThresholdCents === '0';

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Owner approval"
        subtitle="For maintenance spend"
        aside={
          mayChange && !editing ? (
            <div className="flex items-center gap-3">
              {form.justSaved ? <SavedNote /> : null}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setValue(
                    settings.maintenanceApprovalThresholdCents === null
                      ? ''
                      : formatAmount(BigInt(settings.maintenanceApprovalThresholdCents)),
                  );
                  setEditing(true);
                }}
              >
                Change
              </Button>
            </div>
          ) : null
        }
      />

      <div className="px-5 pb-5">
        {/* A property with its own agreed figure overrides this on the property itself; a change
            here applies to future approvals only. */}
        <Limit
          value={
            !recorded ? (
              <span className="text-ink-subtle">Not set</span>
            ) : askAlways ? (
              'Every repair'
            ) : (
              <>
                <span className="text-[0.8125rem] font-medium text-ink-muted">Above </span>
                <Amount
                  value={BigInt(settings.maintenanceApprovalThresholdCents ?? '0')}
                  currency
                />
              </>
            )
          }
          note={
            !recorded
              ? 'Record your management agreement’s limit'
              : askAlways
                ? 'The owner agrees to every repair first'
                : 'Properties can set their own'
          }
        />

        {editing ? (
          <form
            onChange={form.touch}
            className="mt-4 flex flex-wrap items-end gap-2 rounded-md bg-surface-sunk p-4"
            onSubmit={(event) => {
              event.preventDefault();
              try {
                // Empty CLEARS it rather than meaning zero — the opposite of the threshold above,
                // and it has to be: zero here is a real and much stricter setting.
                const cents = value.trim() === '' ? null : parseAmount(value);
                save.mutate({ thresholdCents: cents === null ? null : cents.toString() });
              } catch {
                notifyProblems(
                  'Could not change the maintenance threshold',
                  ['That is not an amount. Write it like 500.00'],
                  MAINTENANCE_THRESHOLD,
                );
              }
            }}
          >
            <Field
              label="Threshold"
              hint="Empty removes it · 0 means every repair"
              className="w-full"
            >
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  inputMode="decimal"
                  disabled={save.isPending}
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value);
                  }}
                  placeholder="500.00"
                  className="tabular-nums"
                />
              )}
            </Field>
            <Button size="sm" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving' : 'Save limit'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => {
                form.reset();
                setEditing(false);
                dismiss(MAINTENANCE_THRESHOLD);
              }}
            >
              Cancel
            </Button>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * The controls that are not settings.
 *
 * SAID OUT LOUD RATHER THAN LEFT IMPLICIT. An agency asking "what protects us here" deserves the
 * whole answer, and two thirds of it is not adjustable — which is itself the design. A 24-hour
 * hold an agency could turn down is a hold that gets turned down the first time an owner rings
 * wanting their money today.
 */
function FixedControls() {
  return (
    <Panel className="p-5">
      <PanelHeader title="Always on" subtitle="Cannot be switched off" className="p-0 pb-3" />
      {/*
       * - New or changed payee accounts are held; a trust administrator can release one early with
       *   an audited written reason.
       * - Payment lines, amounts and destinations are re-checked against the approved run's hash.
       * - An approved run cannot be edited; a correction is a cancel and a new run, both on record.
       * - Only a trust administrator changes a payee account, with 2FA in the last 15 minutes.
       */}
      <Checklist>
        <ChecklistRow label={`${String(PAYEE_QUARANTINE_HOURS)}-hour hold on new payee accounts`} />
        <ChecklistRow label="Approved runs re-checked before paying" />
        <ChecklistRow label="Approved runs locked" />
        <ChecklistRow label="Payee changes need fresh 2FA" />
      </Checklist>
    </Panel>
  );
}

/** The limit as a figure and one line — the explanation lives in the panel's docblock. */
function Limit({ value, note }: { value: React.ReactNode; note: string }) {
  return (
    <div className="rounded-md bg-surface-sunk px-4 py-3">
      <p className="text-[1.25rem] font-bold tracking-[-0.02em]">{value}</p>
      <p className="mt-0.5 text-[0.75rem] text-ink-muted">{note}</p>
    </div>
  );
}

/**
 * The agency's OWN trust account — the account a direct credit batch is drawn on.
 *
 * IT IS THE OTHER END OF §5, AND IT LOOKS ALMOST THE SAME AS AN OWNER'S PAYEE RECORD. It is worth
 * saying out loud why it has none of that screen's controls: no 24-hour quarantine, no version
 * chain, no notification to a previous address. Those exist because an attacker who changes where
 * somebody ELSE's money is sent gets paid, and the owner finds out a month later. Changing this
 * number does not send money anywhere new — it changes which of the agency's own accounts a batch
 * is drawn ON, and the failure is a file the bank rejects, or a batch drawn on the wrong pool of
 * beneficiaries. Loud, same day, and recoverable.
 *
 * WHAT IT DOES SHARE is who may do it. Only a trust_admin, and the act is audited, for the reason
 * every other change to payment routing in this product is.
 *
 * MASKED HERE, LIKE EVERYWHERE ELSE (docs/SECURITY.md §4). The last four is enough for somebody to
 * confirm the account they know, and this screen is not the one exception §4 makes — that one is
 * the field the number is typed INTO, which is why the form below starts empty rather than
 * pre-filled with the digits.
 */
function TrustAccountPanel({ mayChange }: { mayChange: boolean }) {
  const form = useUnsavedChanges();
  const queryClient = useQueryClient();
  const accounts = useQuery(
    trpc.bankFile.trustAccounts.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load the trust accounts' },
      staleTime: 30_000,
    }),
  );
  const [editing, setEditing] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [format, setFormat] = useState<BankFileFormat | ''>('');
  // Which account's row says "Saved" — the form that saved has folded away, and there are several.
  const [savedAccount, setSavedAccount] = useState<string | null>(null);

  const save = useMutation(
    trpc.bankFile.setTrustAccount.mutationOptions({
      meta: { errorTitle: 'Could not record those bank details' },
      onSuccess: (_saved, input) => {
        form.saved();
        setSavedAccount(input.ledgerAccountId);
        dismiss(TRUST_ACCOUNT);
        setEditing(null);
        void queryClient.invalidateQueries({ queryKey: trpc.bankFile.trustAccounts.queryKey() });
      },
    }),
  );

  return (
    <Panel className="p-0">
      <PanelHeader
        title={
          <span className="flex items-center gap-2">
            <Icon name="bank" size={20} className="text-accent" />
            Trust accounts
          </span>
        }
        subtitle="Payment files are drawn on these"
      />

      <div className="px-5 pb-5">
        {accounts.isPending ? (
          <div
            role="status"
            aria-label="Loading trust accounts"
            className="h-24 rounded-md bg-surface-sunk motion-safe:animate-pulse"
          />
        ) : accounts.error ? (
          <LoadFailed inline retry={() => void accounts.refetch()} retrying={accounts.isFetching} />
        ) : accounts.data && accounts.data.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {accounts.data.map((account) => (
              <li key={account.ledgerAccountId} className="rounded-md bg-surface-sunk p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[0.875rem] font-semibold text-ink">{account.name}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.8125rem] text-ink-muted">
                      {account.ready ? (
                        <>
                          <Chip tone="positive">Ready for bank files</Chip>
                        </>
                      ) : (
                        /*
                         * NOT AN ERROR, and not styled as one. An agency that has not recorded this
                         * yet has done nothing wrong — until they run a disbursement, nothing needs
                         * it. What it must not do is stay quiet and then refuse at four o'clock on
                         * the 20th, which is why it says what it is for.
                         */
                        <Chip tone="warn">Setup required</Chip>
                      )}
                    </div>
                  </div>

                  {mayChange && editing !== account.ledgerAccountId ? (
                    <div className="flex items-center gap-3">
                      {form.justSaved && savedAccount === account.ledgerAccountId ? (
                        <SavedNote />
                      ) : null}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          // DELIBERATELY EMPTY, never pre-filled with the digits. §4's one exception
                          // is the field a number is being typed into; a form that showed the whole
                          // number to everybody who pressed Change would make it the rule.
                          setNumber('');
                          setName(account.bankAccountName ?? '');
                          setFormat(account.format ?? '');
                          dismiss(TRUST_ACCOUNT);
                          setEditing(account.ledgerAccountId);
                        }}
                      >
                        {account.ready ? 'Edit details' : 'Add details'}
                      </Button>
                    </div>
                  ) : null}
                </div>

                {account.ready ? (
                  <dl className="mt-4 grid gap-3 border-t border-rule pt-3 sm:grid-cols-2 text-[0.8125rem]">
                    <div>
                      <dt className="text-[0.75rem] text-ink-muted">Account number</dt>
                      <dd className="mt-1 font-mono">{account.maskedAccountNumber}</dd>
                    </div>
                    <div>
                      <dt className="text-[0.75rem] text-ink-muted">Bank file format</dt>
                      <dd className="mt-1 font-medium">{account.formatLabel ?? 'No format set'}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="mt-3 text-[0.75rem] text-ink-muted">
                    Needed before a payment file can be made.
                  </p>
                )}

                {editing === account.ledgerAccountId ? (
                  <form
                    onChange={form.touch}
                    className="mt-4 grid gap-3 border-t border-rule pt-4 sm:grid-cols-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (format === '') {
                        notifyProblems(
                          'Could not record those bank details',
                          ['Choose the format your bank takes'],
                          TRUST_ACCOUNT,
                        );
                        return;
                      }
                      save.mutate({
                        ledgerAccountId: account.ledgerAccountId,
                        bankAccountNumber: number.trim(),
                        bankAccountName: name.trim(),
                        bankFileFormat: format,
                      });
                    }}
                  >
                    <Field
                      label="Account number"
                      required
                      /*
                       * Any of the shapes a person writes it in — the server normalises. A form
                       * that refused `12 3456 0123456 00` teaches an operator to retype a number
                       * they already had right, which is the moment a digit changes.
                       */
                      hint="12-3456-0123456-00"
                    >
                      {(props) => (
                        <Input
                          {...props}
                          autoFocus
                          value={number}
                          onChange={(event) => {
                            setNumber(event.target.value);
                          }}
                          placeholder="12-3456-0123456-00"
                          className="font-mono tabular-nums"
                        />
                      )}
                    </Field>

                    <Field
                      label="Account name"
                      required
                      /*
                       * ONE NAME, USED TWICE. What is typed here is both what the bank holds and
                       * what this account is called everywhere in ProManage — the ledger, the
                       * reconciliation, the commission transfer, and the file's own header. Two
                       * names for one account is an operator checking a screen against their
                       * bank's import screen and having to already know they are the same thing.
                       */
                      hint="As the bank holds it — used everywhere"
                    >
                      {(props) => (
                        <Input
                          {...props}
                          value={name}
                          onChange={(event) => {
                            setName(event.target.value);
                          }}
                        />
                      )}
                    </Field>

                    <Field
                      label="Bank file format"
                      required
                      hint="The one your bank imports"
                      className="sm:col-span-2"
                    >
                      {(props) => (
                        <Select
                          {...props}
                          value={format}
                          onChange={(event) => {
                            setFormat(event.target.value as BankFileFormat | '');
                          }}
                        >
                          <option value="">Choose one</option>
                          {BANK_FILE_FORMATS.map((option) => (
                            <option key={option} value={option}>
                              {BANK_FILE_FORMAT_LABELS[option]}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>

                    <div className="flex items-center gap-2 sm:col-span-2">
                      <Button size="sm" type="submit" disabled={save.isPending}>
                        {save.isPending ? 'Saving' : 'Save details'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          form.reset();
                          setEditing(null);
                          dismiss(TRUST_ACCOUNT);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[0.8125rem] text-ink-muted">
            This agency has no active trust bank account yet.
          </p>
        )}

        {/* The file preview shows the payment count, total and account checksum to check with. */}
        <p className="mt-3 text-[0.75rem] text-ink-muted">
          Check your first payment file with your bank.
        </p>
      </div>
    </Panel>
  );
}

/**
 * The agency's OWN account — where commission goes when it is drawn out of the trust account.
 *
 * DIRECTLY BELOW THE TRUST ACCOUNT, AND THAT ADJACENCY IS THE POINT. Invariant 5 is that trust
 * money and the agency's own money are separate — and the way an agency breaks it is not by
 * deciding to, it is by having one bank account and never noticing the distinction. Two panels,
 * one under the other, each asking for a different number, is the clearest statement of the rule
 * this screen can make. Entering the same number in both is refused, here and in the database.
 *
 * NO FORMAT. A format says how a bank wants to be WRITTEN TO, and nothing is ever written to this
 * account: it is a destination. The file that pays it is drawn on the trust account and written
 * in the trust account's format.
 *
 * MASKED, and the form starts empty, for the same §4 reason as the panel above.
 */
function OperatingAccountPanel({ mayChange }: { mayChange: boolean }) {
  const form = useUnsavedChanges();
  const queryClient = useQueryClient();
  const account = useQuery(
    trpc.bankFile.operatingAccount.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load the operating account' },
      staleTime: 30_000,
    }),
  );
  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');

  const save = useMutation(
    trpc.bankFile.setOperatingAccount.mutationOptions({
      meta: { errorTitle: 'Could not record those bank details' },
      onSuccess: () => {
        form.saved();
        dismiss(OPERATING_ACCOUNT);
        setEditing(false);
        void queryClient.invalidateQueries({ queryKey: trpc.bankFile.operatingAccount.queryKey() });
      },
    }),
  );

  return (
    <Panel className="p-0">
      <PanelHeader
        title={
          <span className="flex items-center gap-2">
            <Icon name="payout" size={20} className="text-accent" />
            Operating account
          </span>
        }
        // Must differ from every trust account — refused here and in the database (invariant 5).
        subtitle="Commission is paid into this — never a trust account"
        aside={
          mayChange && !editing && account.data ? (
            <div className="flex items-center gap-3">
              {form.justSaved ? <SavedNote /> : null}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  // Empty, never pre-filled — §4's one exception is the field being typed into.
                  setNumber('');
                  setName(account.data?.bankAccountName ?? '');
                  dismiss(OPERATING_ACCOUNT);
                  setEditing(true);
                }}
              >
                {account.data?.recorded ? 'Edit details' : 'Add details'}
              </Button>
            </div>
          ) : null
        }
      />

      <div className="px-5 pb-5">
        {account.isPending ? (
          <div
            role="status"
            aria-label="Loading operating account"
            className="h-24 rounded-md bg-surface-sunk motion-safe:animate-pulse"
          />
        ) : account.error ? (
          <LoadFailed inline retry={() => void account.refetch()} retrying={account.isFetching} />
        ) : account.data?.recorded ? (
          <div className="rounded-md bg-surface-sunk p-4">
            <Chip tone="positive">Recorded</Chip>
            <dl className="mt-4 grid gap-3 text-[0.8125rem] sm:grid-cols-2">
              <div>
                <dt className="text-[0.75rem] text-ink-muted">Account name</dt>
                <dd className="mt-1 font-semibold">{account.data.bankAccountName}</dd>
              </div>
              <div>
                <dt className="text-[0.75rem] text-ink-muted">Account number</dt>
                <dd className="mt-1 font-mono">{account.data.maskedAccountNumber}</dd>
              </div>
            </dl>
          </div>
        ) : (
          /*
           * Not an error, like the trust panel's equivalent. An agency that has not recorded this
           * has done nothing wrong — until they draw their fees out, nothing needs it.
           */
          <div className="rounded-md bg-surface-sunk p-4">
            <Chip tone="warn">Setup required</Chip>
            <p className="mt-2 text-[0.75rem] text-ink-muted">
              Needed before commission can be transferred.
            </p>
          </div>
        )}

        {editing ? (
          <form
            onChange={form.touch}
            className="mt-3 grid gap-3 rounded-md bg-surface-sunk p-3 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate({ bankAccountNumber: number.trim(), bankAccountName: name.trim() });
            }}
          >
            <Field label="Account number" required hint="12-3456-0123456-00">
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  value={number}
                  onChange={(event) => {
                    setNumber(event.target.value);
                  }}
                  placeholder="12-3456-0123456-00"
                  className="font-mono tabular-nums"
                />
              )}
            </Field>

            <Field label="Account name" required hint="As the bank holds it">
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                />
              )}
            </Field>

            <div className="flex items-center gap-2 sm:col-span-2">
              <Button size="sm" type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving' : 'Save details'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => {
                  form.reset();
                  setEditing(false);
                  dismiss(OPERATING_ACCOUNT);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}
